import { useRef, useState } from 'react';
import { Link as RouterLink, Location, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grow,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../context/authContext';
import { loginSchema, LoginFormValues } from '../../validation/auth';
import { Logo } from '../../components/Logo';
import { brandGradient } from '../../theme/theme';
import { getDefaultRoute, isRouteAuthorized } from '../../utils/routeAuthorization';
import { LoginIllustration } from './LoginIllustration';

const REMEMBER_EMAIL_KEY = 'imc-remembered-email';

/** Intermittent login 429 investigation (§11/§12/§24) — one clear message
 * per meaningful status, instead of a single generic fallback for
 * everything. 429 specifically reads the server's own Retry-After
 * header/body rather than a hardcoded countdown, so it can never drift
 * from whatever the backend's actual rate-limit window is. */
const describeLoginError = (err: unknown): string => {
  if (!isAxiosError(err)) return 'Something went wrong. Please try again.';

  const status = err.response?.status;
  if (status === 429) {
    const headerSeconds = Number(err.response?.headers?.['retry-after']);
    const bodySeconds = Number((err.response?.data as { retryAfterSeconds?: unknown })?.retryAfterSeconds);
    const seconds = Number.isFinite(headerSeconds) && headerSeconds > 0 ? headerSeconds : bodySeconds;
    return Number.isFinite(seconds) && seconds > 0
      ? `Too many login attempts. Please try again in ${Math.ceil(seconds)} seconds.`
      : 'Too many login attempts. Please wait a moment and try again.';
  }
  if (status === 401) return 'Invalid email or password.';
  if (status === 400 || status === 422) return 'Please check your email and password and try again.';
  if (status === 403) return 'Access denied.';
  return 'Something went wrong. Please try again.';
};

export const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_EMAIL_KEY));
  // Defense-in-depth against a double-submit alongside react-hook-form's
  // own `isSubmitting`-disabled button (§3/§4): a synchronous ref check
  // that can never race with React's own state-commit timing, so two
  // near-simultaneous submit events (e.g. a fast double-click, or Enter
  // plus a click) can never both reach the login API call.
  const submitInFlightRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: localStorage.getItem(REMEMBER_EMAIL_KEY) ?? '' },
  });

  const onSubmit = async (values: LoginFormValues) => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setServerError(null);
    try {
      const loggedInUser = await login(values.email, values.password);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
      // Counselor-login-lands-on-/users correction: a deep link the user
      // was on before being bounced to /login (location.state.from) is
      // only honored if THIS user is actually authorized for it — never
      // blindly restored, since it could belong to whatever page an
      // entirely different, more-privileged session last visited in this
      // browser. Falls back to the first page this user's own permissions
      // actually allow, never a hard-coded route.
      const requestedFrom = (location.state as { from?: Location })?.from?.pathname;
      const redirectTo =
        requestedFrom && isRouteAuthorized(requestedFrom, loggedInUser)
          ? requestedFrom
          : getDefaultRoute(loggedInUser);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setServerError(describeLoginError(err));
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '1 1 46%',
          position: 'relative',
          background: brandGradient,
          color: '#fff',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: 'rgba(255,255,255,0.94)',
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <Logo height={32} />
        </Box>

        <Box sx={{ alignSelf: 'center', width: '100%', maxWidth: 380 }}>
          <LoginIllustration />
        </Box>

        <Box>
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            Nurturing Caring Minds for the World
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 420 }}>
            One workspace for leads, admissions, payments and travel — built for counselors who move
            students from inquiry to departure.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: '1 1 54%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
        }}
      >
        <Grow in timeout={400}>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
              <Logo height={44} />
            </Box>

            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Sign in to IMC
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Welcome back — enter your details to access your workspace.
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2.5}>
                {serverError && <Alert severity="error">{serverError}</Alert>}

                <TextField
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  fullWidth
                  autoFocus
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  {...register('email')}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Remember me</Typography>}
                  />
                  <Link component={RouterLink} to="/forgot-password" variant="body2">
                    Forgot password?
                  </Link>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  fullWidth
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                  {isSubmitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Grow>
      </Box>
    </Box>
  );
};
